import { createFileRoute, Link } from "@tanstack/react-router";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";

const SITE_URL = "https://elvion-group.nl";
const URL = `${SITE_URL}/gids-laadpaal-kosten`;

const TITLE =
  "Kosten laadpaal thuis: wat kost een laadpaal incl. installatie? (2026)";
const DESCRIPTION =
  "Wat kost een laadpaal thuis in 2026? Compleet overzicht van hardware, installatiekosten, meterkast-aanpassingen en besparingstips voor thuisladen.";

const COST_ROWS: { item: string; price: string; note: string }[] = [
  {
    item: "Basis laadpaal 11 kW (1-fase of 3-fase)",
    price: "€ 699 – € 1.099",
    note: "Bekende merken zoals Alfen, Wallbox, EVBox",
  },
  {
    item: "Slimme laadpaal met dynamic load balancing",
    price: "€ 999 – € 1.499",
    note: "Past laadvermogen aan op je meterkast",
  },
  {
    item: "Laadpaal met geïntegreerde kWh-meter (MID)",
    price: "+ € 100 – € 250",
    note: "Nodig voor zakelijk terugvorderen of verrekening",
  },
  {
    item: "Standaard installatie (≤ 15 m kabel, 1 groep)",
    price: "€ 450 – € 750",
    note: "Inclusief aardlekautomaat en oplevering",
  },
  {
    item: "Meterkast verzwaren (1×25A → 3×25A)",
    price: "€ 750 – € 1.500",
    note: "Inclusief aanvraag bij netbeheerder",
  },
  {
    item: "Extra meters kabel, sleuven of gevelpassage",
    price: "€ 8 – € 15 / meter",
    note: "Afhankelijk van ondergrond en route",
  },
];

const FAQS = [
  {
    q: "Wat kost een laadpaal thuis gemiddeld inclusief installatie?",
    a: "Reken in 2026 op € 1.400 – € 2.200 voor een complete thuislaadpaal inclusief montage, kabelwerk en oplevering. Slimme modellen met load balancing of een MID-meter liggen iets hoger; een basis 11 kW paal met korte kabelroute kan onder de € 1.500 blijven.",
  },
  {
    q: "Wat kost alleen de laadpaal (hardware) thuis?",
    a: "De laadpaal zelf kost meestal tussen € 699 en € 1.499. Het verschil zit in laadvermogen (3,7 / 7,4 / 11 / 22 kW), slimme functies (app, dynamisch laden, zonne-overschot) en certificering zoals MID voor kWh-registratie.",
  },
  {
    q: "Hoeveel kost de installatie van een laadpaal thuis?",
    a: "Een standaard installatie (tot ~15 meter kabel, vrije route, voldoende ruimte in de meterkast) kost € 450 – € 750. Langere kabelroutes, doorvoeringen, graafwerk of een tweede groepenkast verhogen de prijs.",
  },
  {
    q: "Moet mijn meterkast worden aangepast voor een laadpaal?",
    a: "Bij 11 kW laden heb je een 3×25A aansluiting nodig. Heb je nu 1×25A of 1×35A, dan moet de netbeheerder de aansluiting verzwaren (€ 350 – € 700 eenmalig) en is meestal ook een aanpassing in de meterkast nodig (€ 400 – € 800).",
  },
  {
    q: "Is er subsidie of belastingvoordeel voor een laadpaal thuis?",
    a: "Voor particulieren is er momenteel geen landelijke subsidie. Zakelijke rijders en zzp'ers kunnen de laadpaal vaak via KIA en MIA fiscaal aftrekken, en werkgevers kunnen de laadpaal onbelast vergoeden onder de WKR.",
  },
  {
    q: "Kan ik een laadpaal combineren met zonnepanelen?",
    a: "Ja. Met dynamic solar charging laad je je auto alleen met je eigen overschot zonnestroom. Daarmee bespaar je netto € 0,15 – € 0,30 per kWh ten opzichte van laden uit het net.",
  },
];

export const Route = createFileRoute("/gids-laadpaal-kosten")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { name: "robots", content: "index,follow" },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:url", content: URL },
      { property: "og:type", content: "article" },
    ],
    links: [{ rel: "canonical", href: URL }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Article",
          headline: TITLE,
          description: DESCRIPTION,
          mainEntityOfPage: URL,
          author: { "@type": "Organization", name: "Elvion Group Nederland" },
          publisher: { "@type": "Organization", name: "Elvion Group Nederland" },
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: FAQS.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: SITE_URL },
            { "@type": "ListItem", position: 2, name: "Shop", item: `${SITE_URL}/c` },
            { "@type": "ListItem", position: 3, name: "Kosten laadpaal thuis", item: URL },
          ],
        }),
      },
    ],
  }),
  component: GuidePage,
  errorComponent: ({ error }) => (
    <main className="mx-auto max-w-3xl p-10 text-center">
      <h1 className="text-2xl font-bold">Er ging iets mis</h1>
      <p className="mt-2 text-text-secondary">{error instanceof Error ? error.message : "Onbekende fout"}</p>
    </main>
  ),
  notFoundComponent: () => (
    <main className="mx-auto max-w-3xl p-10 text-center">
      <h1 className="text-2xl font-bold">Pagina niet gevonden</h1>
      <Link to="/" className="mt-4 inline-block text-orange hover:underline">
        Terug naar home
      </Link>
    </main>
  ),
});

function GuidePage() {
  return (
    <>
      <Header />
      <main className="mx-auto max-w-4xl px-4 py-10 sm:px-6 lg:px-8">

        <h1 className="text-3xl font-bold tracking-tight sm:text-4xl">
          Wat kost een laadpaal thuis? Volledige kostengids 2026
        </h1>
        <p className="mt-4 max-w-3xl text-base text-text-secondary sm:text-lg">
          Overweeg je een laadpaal thuis? In deze gids zetten we alle kosten op een rij:
          van de hardware en installatie tot eventuele meterkast-aanpassingen, kabelwerk
          en slimme functies. Zo weet je vooraf precies waar je aan toe bent.
        </p>

        <section className="mt-10">
          <h2 className="text-2xl font-bold tracking-tight">Kort antwoord: gemiddelde kosten</h2>
          <p className="mt-3 text-text-secondary">
            Een complete thuislaadpaal kost in 2026 gemiddeld <strong>€ 1.400 – € 2.200
            inclusief installatie</strong>. De hardware ligt tussen € 699 en € 1.499, en
            een standaard installatie tussen € 450 en € 750. Bij oudere meterkasten of
            lange kabelroutes kan het totaal oplopen tot circa € 3.000.
          </p>
        </section>

        <section className="mt-10">
          <h2 className="text-2xl font-bold tracking-tight">Kostenoverzicht laadpaal thuis</h2>
          <div className="mt-4 overflow-x-auto rounded-xl border border-border bg-card">
            <table className="w-full text-left text-sm">
              <thead className="bg-muted/40 text-foreground">
                <tr>
                  <th className="px-4 py-3 font-semibold">Onderdeel</th>
                  <th className="px-4 py-3 font-semibold">Indicatie prijs</th>
                  <th className="px-4 py-3 font-semibold">Toelichting</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {COST_ROWS.map((r) => (
                  <tr key={r.item}>
                    <td className="px-4 py-3 font-medium text-foreground">{r.item}</td>
                    <td className="px-4 py-3 text-text-secondary">{r.price}</td>
                    <td className="px-4 py-3 text-text-secondary">{r.note}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="mt-3 text-xs text-text-secondary">
            Prijzen indicatief, incl. btw voor particulieren. Definitieve prijs op basis
            van schouw of digitale intake.
          </p>
        </section>

        <section className="mt-10 grid gap-8 lg:grid-cols-2">
          <article>
            <h2 className="text-xl font-bold tracking-tight">1. Hardware: welke laadpaal past bij je?</h2>
            <p className="mt-2 text-text-secondary">
              Voor de meeste thuissituaties is een 11 kW laadpaal (3×16A) de standaard.
              Heb je een 1-fase aansluiting, dan laad je tot 7,4 kW. Kies voor een slim
              model met dynamic load balancing als je meterkast krap zit — dat voorkomt
              een dure verzwaring.
            </p>
          </article>
          <article>
            <h2 className="text-xl font-bold tracking-tight">2. Installatie: wat zit erin?</h2>
            <p className="mt-2 text-text-secondary">
              Een standaard installatie bevat een vrije groep met aardlekautomaat type B,
              kabelwerk (meestal tot 15 m), montage van de paal, ingebruikname en
              oplevering met meetrapport. Werk je met een gevelpaal of vrijstaande paal
              in de tuin, dan komen er meters kabel en eventueel graafwerk bij.
            </p>
          </article>
          <article>
            <h2 className="text-xl font-bold tracking-tight">3. Meterkast en netbeheerder</h2>
            <p className="mt-2 text-text-secondary">
              Veel woningen hebben nog een 1×35A of 1×40A aansluiting. Voor 11 kW laden
              vraag je via je netbeheerder een verzwaring naar 3×25A aan. Dat kost
              eenmalig € 350 – € 700, plus eventueel aanpassingen in de meterkast.
            </p>
          </article>
          <article>
            <h2 className="text-xl font-bold tracking-tight">4. Besparen op je laadkosten</h2>
            <p className="mt-2 text-text-secondary">
              Combineer je laadpaal met zonnepanelen en een dynamisch energiecontract.
              Met smart charging laad je vooral op het goedkoopste uur (vaak 's nachts of
              tijdens piek zonproductie). Dat scheelt al snel € 300 – € 600 per jaar.
            </p>
          </article>
        </section>

        <section className="mt-12">
          <h2 className="text-2xl font-bold tracking-tight">Veelgestelde vragen</h2>
          <div className="mt-4 divide-y divide-border rounded-xl border border-border bg-card">
            {FAQS.map((f) => (
              <details key={f.q} className="group p-5">
                <summary className="cursor-pointer list-none font-semibold text-foreground">
                  {f.q}
                </summary>
                <p className="mt-2 text-text-secondary">{f.a}</p>
              </details>
            ))}
          </div>
        </section>

        <section className="mt-12 rounded-2xl bg-card p-6 ring-1 ring-border sm:p-8">
          <h2 className="text-xl font-bold tracking-tight">
            Benieuwd naar jouw exacte kosten?
          </h2>
          <p className="mt-2 text-text-secondary">
            Bereken in 2 minuten welk laadvermogen past bij jouw auto en meterkast, of
            vraag een offerte aan voor installatie.
          </p>
          <div className="mt-4 flex flex-wrap gap-3">
            <Link
              to="/vermogen-calculator"
              className="inline-flex items-center rounded-md bg-orange px-5 py-2.5 text-sm font-semibold text-white hover:bg-orange/90"
            >
              Start EV-vermogen calculator
            </Link>
            <Link
              to="/info/$slug"
              params={{ slug: "laadpalen" }}
              className="inline-flex items-center rounded-md border border-orange bg-background px-5 py-2.5 text-sm font-semibold text-orange hover:bg-orange hover:text-white"
            >
              Meer over laadpalen
            </Link>
            <Link
              to="/offerte-aanvragen"
              className="inline-flex items-center rounded-md border border-border bg-background px-5 py-2.5 text-sm font-semibold text-foreground hover:border-orange hover:text-orange"
            >
              Vraag offerte aan
            </Link>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}
